import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Avatar,
} from '@mui/material';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveShoppingCartOutlinedIcon from '@mui/icons-material/RemoveShoppingCartOutlined';
import api from '../services/api';

// Markdown patrol (降價巡邏) — owner-only. Two queues fed by the workspace
// patrol script: kind='markdown' (price-cut suggestions, storefront price only,
// Shopee untouched) and kind='delist' (下架 suggestions — preorder model, no
// stock to clear, so hopeless listings are archived, never "sent to outlet").
// Plus a read-only 清倉測試中 panel: delist candidates parked in a clearance
// flash event, which leave the queues above while the test runs.

interface PatrolItem {
  id: number;
  sku: string | null;
  handle: string;
  title: string | null;
  category: string | null;
  current_price: string;
  suggested_price: string | null;
  cur_margin: string | null;
  new_margin: string | null;
  views: number | null;
  units: number | null;
  cvr: string | null;
  band_cvr: string | null;
  lost_units: string | null;
  reason: string | null;
  status: 'pending' | 'applied' | 'dismissed' | 'reverted';
  kind: 'markdown' | 'delist';
  add_to_carts: number | null;
  age_days: number | null;
  old_price: string | null;
  applied_price: string | null;
  test_queued: boolean;
  image_url: string | null;
  product_status: string | null;
  created_at: string;
  resolved_at: string | null;
}

// Clearance test (清倉測試中) — an active `events` row with slug 'clearance-*'.
interface ClearanceTestProduct {
  handle: string;
  sku: string | null;
  title: string | null;
  image_url: string | null;
  price: number | null;
  compare_at_price: number | null;
}

interface ClearanceTest {
  slug: string;
  starts_at: string;
  ends_at: string;
  products: ClearanceTestProduct[];
}

const nt = (v: string | number | null) =>
  v === null || v === undefined ? '—' : `NT$${Math.round(Number(v))}`;
const pct = (v: string | null) =>
  v === null || v === undefined ? '—' : `${(Number(v) * 100).toFixed(1)}%`;
// Days until the test ends, rounded up — a test ending later today reads "剩 1 天".
const daysLeft = (iso: string) => {
  const ms = new Date(iso).getTime() - Date.now();
  return Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / 86400000)) : 0;
};

const STATUS_META: Record<string, { label: string; color: 'default' | 'success' | 'warning' }> = {
  applied: { label: '已處理', color: 'success' },
  dismissed: { label: '已忽略', color: 'default' },
  reverted: { label: '已復原', color: 'warning' },
};

const MarkdownPatrolPage: React.FC = () => {
  const [items, setItems] = useState<PatrolItem[]>([]);
  const [tests, setTests] = useState<ClearanceTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/markdown-patrol');
      setItems(res.data.items || []);
      setTests(res.data.tests || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load markdown patrol', err);
      setError('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: number, action: 'apply' | 'dismiss' | 'revert' | 'archive' | 'unarchive' | 'queue-test') => {
    setBusy(id);
    setError(null);
    try {
      await api.post(`/api/markdown-patrol/${id}/${action}`);
    } catch (err: any) {
      setError(err?.response?.data?.message || `${action} 失敗`);
    } finally {
      setBusy(null);
      load();
    }
  };

  const productCell = (it: PatrolItem) => (
    <TableCell sx={{ minWidth: 220 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Link href={`https://metanoia.tw/products/${it.handle}`} target="_blank" rel="noopener">
          <Avatar
            variant="rounded"
            src={it.image_url || undefined}
            alt={it.title || it.handle}
            sx={{ width: 56, height: 74 }}
          >
            ?
          </Avatar>
        </Link>
        <Box sx={{ minWidth: 0 }}>
          <Link
            href={`https://metanoia.tw/products/${it.handle}`}
            target="_blank"
            rel="noopener"
            underline="hover"
          >
            {it.sku || '—'}
          </Link>
          <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ maxWidth: 170 }}>
            {it.title || it.handle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {it.category || '—'}{it.age_days ? ` · 上架 ${it.age_days} 天` : ''}
          </Typography>
        </Box>
      </Box>
    </TableCell>
  );

  // 加入測試 toggle — the owner's entry gate for the clearance test batch.
  // Queued rows are what clearance_test.py --start picks up.
  const queueTestButton = (it: PatrolItem) => (
    <Button
      size="small"
      variant={it.test_queued ? 'contained' : 'outlined'}
      color={it.test_queued ? 'success' : 'inherit'}
      disabled={busy === it.id}
      onClick={() => act(it.id, 'queue-test')}
      sx={{ mr: 1 }}
    >
      {it.test_queued ? '已排入測試' : '加入測試'}
    </Button>
  );

  const statusChip = (it: PatrolItem) => (
    <Chip
      size="small"
      label={
        it.status === 'applied'
          ? it.applied_price
            ? `已降價 ${nt(it.old_price)}→${nt(it.applied_price)}`
            : '已下架'
          : STATUS_META[it.status]?.label || it.status
      }
      color={STATUS_META[it.status]?.color || 'default'}
    />
  );

  const markdownRow = (it: PatrolItem) => (
    <TableRow key={it.id} hover>
      {productCell(it)}
      <TableCell align="right">{nt(it.current_price)}</TableCell>
      <TableCell align="right">
        {it.suggested_price ? <strong>{nt(it.suggested_price)}</strong> : <Chip size="small" label="降不動" variant="outlined" />}
      </TableCell>
      <TableCell align="right">
        <Tooltip title="現在毛利 → 降價後毛利（官網基準，無蝦皮費 — 降價只動官網）">
          <span>{pct(it.cur_margin)} → {pct(it.new_margin)}</span>
        </Tooltip>
      </TableCell>
      <TableCell align="right">
        <Tooltip title={`60天 ${it.views ?? '—'} 次瀏覽 / ${it.units ?? '—'} 件；同類同價位帶 CVR ${pct(it.band_cvr)}`}>
          <span>
            {pct(it.cvr)} <Typography component="span" variant="caption" color="text.secondary">/ {pct(it.band_cvr)}</Typography>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="right">{it.lost_units ? Math.round(Number(it.lost_units)) : '—'}</TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">{it.reason || ''}</Typography>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {it.status === 'pending' ? (
          <>
            {it.suggested_price ? (
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={<TrendingDownIcon />}
                disabled={busy === it.id}
                onClick={() => act(it.id, 'apply')}
                sx={{ mr: 1 }}
              >
                套用降價
              </Button>
            ) : (
              <>
                {queueTestButton(it)}
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<RemoveShoppingCartOutlinedIcon />}
                  disabled={busy === it.id}
                  onClick={() => act(it.id, 'archive')}
                  sx={{ mr: 1 }}
                >
                  下架
                </Button>
              </>
            )}
            <Button size="small" disabled={busy === it.id} onClick={() => act(it.id, 'dismiss')}>
              忽略
            </Button>
          </>
        ) : (
          <>
            {statusChip(it)}
            {it.status === 'applied' && (
              <Button
                size="small"
                disabled={busy === it.id}
                onClick={() => act(it.id, it.applied_price ? 'revert' : 'unarchive')}
                sx={{ ml: 1 }}
              >
                {it.applied_price ? '復原' : '重新上架'}
              </Button>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );

  const delistRow = (it: PatrolItem) => (
    <TableRow key={it.id} hover>
      {productCell(it)}
      <TableCell align="right">{nt(it.current_price)}</TableCell>
      <TableCell align="right">
        <Tooltip title="60天官網數據：瀏覽 / 加購物車 / 售出">
          <span>
            {it.views ?? '—'} / {it.add_to_carts ?? '—'} / {it.units ?? '—'}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="right">{pct(it.cur_margin)}</TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">{it.reason || ''}</Typography>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {it.status === 'pending' ? (
          <>
            {queueTestButton(it)}
            <Button
              size="small"
              variant="contained"
              color="warning"
              startIcon={<RemoveShoppingCartOutlinedIcon />}
              disabled={busy === it.id}
              onClick={() => act(it.id, 'archive')}
              sx={{ mr: 1 }}
            >
              下架
            </Button>
            <Button size="small" disabled={busy === it.id} onClick={() => act(it.id, 'dismiss')}>
              保留
            </Button>
          </>
        ) : (
          <>
            {statusChip(it)}
            {it.status === 'applied' && (
              <Button size="small" disabled={busy === it.id} onClick={() => act(it.id, 'unarchive')} sx={{ ml: 1 }}>
                重新上架
              </Button>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const markdownPending = items.filter((it) => it.kind === 'markdown' && it.status === 'pending');
  const delistPending = items.filter((it) => it.kind === 'delist' && it.status === 'pending');
  const resolved = items.filter((it) => it.status !== 'pending');

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        降價巡邏
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        高流量低轉換的降價建議＋零成交零興趣的下架建議 — 只動官網，不動蝦皮。降價後原價顯示為劃線價；下架可隨時重新上架。
      </Typography>
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ mb: 3, overflowX: 'auto' }}>
        <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5 }}>
          降價建議（{markdownPending.length}）
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>商品</TableCell>
              <TableCell align="right">現價</TableCell>
              <TableCell align="right">建議價</TableCell>
              <TableCell align="right">毛利</TableCell>
              <TableCell align="right">CVR / 帶均</TableCell>
              <TableCell align="right">60天流失件數</TableCell>
              <TableCell>原因</TableCell>
              <TableCell align="right">動作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {markdownPending.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    目前沒有降價建議
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              markdownPending.map(markdownRow)
            )}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ mb: 3, overflowX: 'auto' }}>
        <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5 }}>
          下架建議（{delistPending.length}）
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, display: 'block' }}>
          上架 60 天以上、有曝光但零成交且幾乎零加車。下架只影響官網，蝦皮照舊。
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>商品</TableCell>
              <TableCell align="right">現價</TableCell>
              <TableCell align="right">瀏覽/加車/售出</TableCell>
              <TableCell align="right">毛利</TableCell>
              <TableCell>診斷</TableCell>
              <TableCell align="right">動作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {delistPending.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    目前沒有下架建議 — 有曝光的商品都有成交或加車
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              delistPending.map(delistRow)
            )}
          </TableBody>
        </Table>
      </Paper>

      {tests.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2">清倉測試中（{tests.length}）</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            下架候補改用限時清倉活動測試需求，測試期間不會出現在上面的清單。純檢視，不需動作。
          </Typography>
          {tests.map((t) => (
            <Paper key={t.slug} variant="outlined" sx={{ mb: 1.5, p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t.slug}
                </Typography>
                <Chip size="small" variant="outlined" label={`剩 ${daysLeft(t.ends_at)} 天`} />
                <Typography variant="caption" color="text.secondary">
                  {t.products.length} 件商品
                </Typography>
              </Box>
              {t.products.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  找不到對應商品（可能已改網址或下架）
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}>
                  {t.products.map((p) => (
                    <Box key={p.handle} sx={{ width: 100, flexShrink: 0 }}>
                      <Link
                        href={`https://metanoia.tw/products/${p.handle}`}
                        target="_blank"
                        rel="noopener"
                      >
                        <Avatar
                          variant="rounded"
                          src={p.image_url || undefined}
                          alt={p.title || p.handle}
                          sx={{ width: 56, height: 74 }}
                        >
                          ?
                        </Avatar>
                      </Link>
                      <Tooltip title={p.title || p.handle}>
                        <Typography variant="caption" display="block" noWrap sx={{ mt: 0.5 }}>
                          {p.sku || '—'}
                        </Typography>
                      </Tooltip>
                      <Typography variant="caption" display="block" color="error.main" noWrap>
                        測試價 {nt(p.price)}
                      </Typography>
                      {p.compare_at_price !== null && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          noWrap
                          sx={{ textDecoration: 'line-through' }}
                        >
                          原價 {nt(p.compare_at_price)}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {resolved.length > 0 && (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5 }}>
            最近 30 天已處理（{resolved.length}）
          </Typography>
          <Table size="small">
            <TableBody>
              {resolved.map((it) => (it.kind === 'delist' ? delistRow(it) : markdownRow(it)))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default MarkdownPatrolPage;
